import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useState } from "react";

export default function Hero() {
    const [hoveredBtn, setHoveredBtn] = useState<"download" | "projects" | null>(null);

    return (
        <section id="home" className="relative pt-32 pb-20 overflow-hidden bg-background">
            {/* Top Right Gradient Blob (Subtle) */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-neon/10 rounded-full blur-[120px] -z-10 translate-x-1/3 -translate-y-1/3" />

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="flex flex-col items-start text-left"
                    >
                        <div className="inline-flex items-center rounded-full bg-neon/10 px-4 py-1.5 text-xs font-bold text-neon mb-6 tracking-wider">
                            <span className="w-2 h-2 rounded-full bg-neon mr-2 animate-pulse" />
                            Available for new projects
                        </div>

                        <h1 className="text-5xl md:text-7xl font-display font-black tracking-tight leading-[1.1] mb-6 text-slate-900">
                            Musab Digale
                        </h1>

                        <h2 className="text-xl md:text-2xl font-display font-medium mb-8 text-slate-500 max-w-lg">
                            A Software Developer, <span className="text-neon font-bold">TechBusiness Builder</span> and AI Automation Engineer.
                        </h2>

                        <div
                            className="inline-flex flex-col sm:flex-row p-1.5 sm:p-2 bg-slate-100/80 border border-slate-200/80 rounded-[2rem] sm:rounded-full mb-12 shadow-sm w-full sm:w-auto gap-2 sm:gap-1"
                            onMouseLeave={() => setHoveredBtn(null)}
                        >
                            <a
                                href="/img/resume.pdf"
                                download="Musab_Digale_Resume.pdf"
                                className="w-full sm:w-auto"
                                onMouseEnter={() => setHoveredBtn("download")}
                            >
                                <Button
                                    className={`h-12 sm:h-14 px-6 sm:px-8 rounded-full font-bold text-base transition-all duration-300 shadow-md w-full sm:w-auto flex items-center justify-center gap-2 ${hoveredBtn === "projects"
                                        ? "bg-transparent text-slate-500 hover:bg-transparent shadow-none"
                                        : "bg-neon text-black hover:bg-neon"
                                        }`}
                                >
                                    Download CV
                                    {hoveredBtn !== "projects" && <ArrowUpRight className="w-5 h-5 stroke-[2.5]" />}
                                </Button>
                            </a>
                            <a href="#projects" className="w-full sm:w-auto">
                                <Button
                                    variant="ghost"
                                    onMouseEnter={() => setHoveredBtn("projects")}
                                    className={`h-12 sm:h-14 px-6 sm:px-8 rounded-full font-semibold text-base transition-all duration-300 w-full sm:w-auto flex items-center justify-center gap-2 ${hoveredBtn === "projects"
                                        ? "bg-neon text-black hover:bg-neon hover:text-black shadow-md"
                                        : "bg-transparent text-slate-500 hover:bg-transparent shadow-none"
                                        }`}
                                >
                                    View Projects
                                    {hoveredBtn === "projects" && <ArrowUpRight className="w-5 h-5 stroke-[2.5]" />}
                                </Button>
                            </a>
                        </div>


                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                        className="relative block mt-12 lg:mt-0"
                    >
                        {/* Soft Glow Background */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-slate-100/50 rounded-full blur-3xl -z-10" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] h-[220px] md:w-[400px] md:h-[400px] border border-slate-100 rounded-full -z-10" />

                        <div className="w-full max-w-sm md:max-w-md mx-auto relative z-10 flex justify-center">
                            {/* Hero Image */}
                            <div className="relative w-[300px] h-[300px] md:w-[480px] md:h-[480px] flex items-center justify-center">
                                <img
                                    src="/img/hr.png"
                                    alt="Musab Digale"
                                    className="w-full h-full object-contain z-10 relative"
                                />
                            </div>

                            {/* Floating Card 1: Email/Congrats (Top Left) */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.6, duration: 0.5 }}
                                className="absolute top-4 -left-2 md:top-10 md:-left-4 z-20 bg-white p-2 md:p-3 pr-4 md:pr-6 rounded-xl md:rounded-2xl shadow-lg flex items-center gap-2 md:gap-3 border border-slate-100 scale-90 md:scale-100"
                            >
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                                </div>
                                <div>
                                    <p className="text-[10px] md:text-xs text-orange-500 font-bold">Email!</p>
                                    <p className="text-[10px] md:text-xs font-semibold text-slate-700">You have got an Email</p>
                                </div>
                                <div className="absolute -top-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-neon rounded-full border-2 border-white" />
                            </motion.div>

                            {/* Floating Card 2: Jobholder/Stats (Bottom Right) */}
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.8, duration: 0.5 }}
                                className="absolute bottom-4 -right-4 md:bottom-10 md:-right-8 z-20 bg-white p-3 md:p-4 rounded-xl md:rounded-2xl shadow-xl flex items-center gap-3 md:gap-4 border border-slate-100 scale-90 md:scale-100"
                            >
                                <div className="flex -space-x-2">
                                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#7c3aed] border-2 border-white shadow-sm" />
                                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#a78bfa] border-2 border-white shadow-sm" />
                                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-neon border-2 border-white shadow-sm" />
                                </div>
                                <div>
                                    <p className="text-[10px] md:text-xs text-slate-500 font-bold">20+ Projects</p>
                                    <p className="text-xs md:text-sm font-black text-slate-900">Completed</p>
                                </div>
                            </motion.div>

                            {/* Floating Card 3: Icon (Top Right) */}
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 1, duration: 0.4 }}
                                className="absolute top-20 -right-2 md:top-28 md:-right-4 z-20 bg-white p-2 md:p-3 rounded-lg md:rounded-xl shadow-lg border border-slate-100 flex items-center gap-2 md:gap-3 scale-90 md:scale-100"
                            >
                                <div className="w-6 h-6 md:w-8 md:h-8 rounded-md md:rounded-lg bg-red-50 flex items-center justify-center text-red-500">
                                    <span className="font-bold text-[10px] md:text-xs">PDF</span>
                                </div>
                                <div className="text-[10px] md:text-xs font-semibold text-slate-700">
                                    <p>Resume</p>
                                    <p className="text-[8px] md:text-[10px] text-slate-400">1.2 MB</p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
